<script setup lang="ts">
import { watch, ref, onMounted } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { EMIT, MEDICAID_FEATUREABLE_TYPE, PERMISSIONS } from '@/enum';
import { PersonStores, useRelayHubStore } from '@/store';
import { can } from '@/composables';
import { storeToRefs } from 'pinia';

const treatmentPlansStore = PersonStores.useUserTreatmentPlansStore();
const profileStore = PersonStores.useUserProfileStore();
const relayHubStore = useRelayHubStore();

const { userTreatmentPlans } = storeToRefs(treatmentPlansStore);
const { medicaidBilling } = storeToRefs(profileStore);

const diagnosis_code = ref(userTreatmentPlans.value?.diagnosis_code || '');
const medicaidEnabled = ref<boolean>(medicaidBilling?.value?.medicaid_enabled as boolean);

const emit = defineEmits([EMIT.UPDATE]);

watch(
  () => userTreatmentPlans.value,
  (value) => {
    diagnosis_code.value = value?.diagnosis_code || '';
  },
);

watch(
  () => medicaidBilling?.value,
  (value) => {
    medicaidEnabled.value = value?.medicaid_enabled;
  },
);

const rules = {
  diagnosis_code: { required },
};

const v$ = useVuelidate(rules, { diagnosis_code });

const resetDirty = () => v$.value.$reset();

const onSave = async () => {
  if (userTreatmentPlans.value && diagnosis_code.value !== userTreatmentPlans.value?.diagnosis_code) {
    userTreatmentPlans.value.diagnosis_code = diagnosis_code.value || '';
    await treatmentPlansStore.updateTreatmentPlan();
    treatmentPlansStore.doToast('Diagnosis Code has been added');
    emit(EMIT.UPDATE);
  }
  if (medicaidBilling?.value?.medicaid_enabled !== medicaidEnabled.value) {
    await toggleMedicaid();
    await getBillingInfo();
  }

  resetDirty();
};

const getBillingInfo = async () => {
  if (can([PERMISSIONS.MEDICAID_MANAGE_CLINICIANS])) {
    await profileStore.getMedicaidBilling();
    if (profileStore.error) profileStore.doToast(profileStore.error as string, 'Error', true);
  }
};

const onCancel = () => {
  diagnosis_code.value = userTreatmentPlans.value?.diagnosis_code || '';
  resetDirty();
};

const toggleMedicaid = async () => {
  const personId: number = userTreatmentPlans.value?.person_id as number;

  if (medicaidEnabled.value) {
    await relayHubStore.enableMedicaid([personId], MEDICAID_FEATUREABLE_TYPE.PEOPLE);
    if (!relayHubStore.error) relayHubStore.doToast(`Student is now Medicaid Enabled.`);
    else relayHubStore.doToast(relayHubStore.message as string, 'Error', true);
  } else {
    await relayHubStore.disableMedicaid([personId], MEDICAID_FEATUREABLE_TYPE.PEOPLE);
    if (!relayHubStore.error) relayHubStore.doToast(`Student is now Medicaid Disabled.`);
    else relayHubStore.doToast(relayHubStore.message as string, 'Error', true);
  }
};

onMounted(async () => await getBillingInfo());
</script>

<template>
  <FormCard title="Medicaid" :cancel="onCancel" :save="onSave" :dirty="v$.$anyDirty">
    <template #form>
      <form class="w-full flex flex-wrap">
        <div v-if="can([PERMISSIONS.MEDICAID_MANAGE_CLINICIANS])" class="field col-1">
          <label for="enable" class="block mb-2 text-center">Enable</label>
          <div class="flex align-content-center justify-content-center mt-3">
            <Checkbox
              id="enable"
              v-model="medicaidEnabled"
              size="large"
              :binary="true"
              :disabled="!can([PERMISSIONS.MEDICAID_MANAGE_CLINICIANS])"
              @change="v$.$touch"
            />
          </div>
        </div>
        <div class="field col-11">
          <label for="code" class="block mb-2">Diagnosis Code</label>
          <InputText
            id="code"
            v-model="v$.diagnosis_code.$model"
            :disabled="!can([PERMISSIONS.PERSON_TREATMENT_PLANS_STORE])"
            type="url"
            class="inputfield w-full"
            :class="{ 'p-invalid': v$.diagnosis_code.$dirty }"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>

<style lang="scss">
.check {
  height: 2.25rem;
}
</style>
